/**
 * All of the universe's sound, synthesized live with the Web Audio API so there
 * are no audio files to ship or license. Provides a subtle ambient drone plus
 * hover, select, and warp effects. Audio only begins after a user gesture, in
 * line with browser autoplay rules.
 */
export class AudioManager {
  private context: AudioContext | null
  private masterGain: GainNode | null
  private noiseBuffer: AudioBuffer | null
  private isMuted: boolean

  /**
   * Create the manager in a started-but-silent state until a gesture arrives.
   */
  public constructor() {
    this.context = null
    this.masterGain = null
    this.noiseBuffer = null
    this.isMuted = false
  }

  /**
   * Lazily create and resume the audio graph. Safe to call on every gesture.
   *
   * @returns {void}
   */
  public start(): void {
    if (this.context === null) {
      this.buildGraph()
    }
    if (this.context !== null && this.context.state === 'suspended') {
      void this.context.resume()
    }
  }

  /**
   * Mute or unmute all output.
   *
   * @param {boolean} muted - True to silence the universe.
   * @returns {void}
   */
  public setMuted(muted: boolean): void {
    this.isMuted = muted
    if (this.context === null || this.masterGain === null) {
      return
    }
    const target = muted ? 0 : 0.9
    this.masterGain.gain.cancelScheduledValues(this.context.currentTime)
    this.masterGain.gain.setTargetAtTime(target, this.context.currentTime, 0.2)
  }

  /**
   * Whether the universe is currently muted.
   *
   * @returns {boolean} True if muted.
   */
  public getMuted(): boolean {
    return this.isMuted
  }

  /**
   * Play the soft chime that accompanies hovering a planet.
   *
   * @returns {void}
   */
  public playHover(): void {
    this.playTone(620, 0.18, 'sine', 0.08)
    this.playTone(930, 0.16, 'sine', 0.04, 0.01)
  }

  /**
   * Play the short rising whoosh that accompanies selecting a planet.
   *
   * @returns {void}
   */
  public playSelect(): void {
    this.playSweep(320, 720, 0.32, 0.12)
    this.playNoiseBurst(0.22, 1200, 0.05)
  }

  /**
   * Play the longer sweep that accompanies the warp into a service.
   *
   * @returns {void}
   */
  public playWarp(): void {
    this.playSweep(180, 1400, 1.2, 0.16)
    this.playNoiseBurst(1.2, 2400, 0.08)
  }

  /**
   * Build the persistent audio graph: master gain, ambient drone, and the shared
   * noise buffer used by burst effects.
   *
   * @returns {void}
   */
  private buildGraph(): void {
    const context = new AudioContext()
    this.context = context

    this.masterGain = context.createGain()
    this.masterGain.gain.value = this.isMuted ? 0 : 0.9
    this.masterGain.connect(context.destination)

    this.noiseBuffer = this.createNoiseBuffer(context)
    this.buildAmbientDrone(context)
  }

  /**
   * Create the slow, breathing ambient drone from two detuned oscillators.
   *
   * @param {AudioContext} context - The audio context to build within.
   * @returns {void}
   */
  private buildAmbientDrone(context: AudioContext): void {
    if (this.masterGain === null) {
      return
    }
    const ambientGain = context.createGain()
    ambientGain.gain.value = 0.05
    ambientGain.connect(this.masterGain)

    const filter = context.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 380
    filter.connect(ambientGain)

    const lowOscillator = context.createOscillator()
    lowOscillator.type = 'sine'
    lowOscillator.frequency.value = 55
    lowOscillator.connect(filter)
    lowOscillator.start()

    const detunedOscillator = context.createOscillator()
    detunedOscillator.type = 'triangle'
    detunedOscillator.frequency.value = 82.5
    detunedOscillator.detune.value = 6
    detunedOscillator.connect(filter)
    detunedOscillator.start()

    const breathe = context.createOscillator()
    breathe.type = 'sine'
    breathe.frequency.value = 0.08
    const breatheGain = context.createGain()
    breatheGain.gain.value = 0.025
    breathe.connect(breatheGain)
    breatheGain.connect(ambientGain.gain)
    breathe.start()
  }

  /**
   * Generate a one-second white-noise buffer reused by burst effects.
   *
   * @param {AudioContext} context - The audio context to build within.
   * @returns {AudioBuffer} The noise buffer.
   */
  private createNoiseBuffer(context: AudioContext): AudioBuffer {
    const length = context.sampleRate
    const buffer = context.createBuffer(1, length, context.sampleRate)
    const data = buffer.getChannelData(0)
    for (let index = 0; index < length; index += 1) {
      data[index] = Math.random() * 2 - 1
    }
    return buffer
  }

  /**
   * Play a single enveloped oscillator tone.
   *
   * @param {number} frequency - The tone frequency in hertz.
   * @param {number} duration - The tone length in seconds.
   * @param {OscillatorType} type - The oscillator waveform.
   * @param {number} peakGain - The envelope peak gain.
   * @param {number} [delaySeconds] - Optional start delay.
   * @returns {void}
   */
  private playTone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    peakGain: number,
    delaySeconds = 0,
  ): void {
    if (this.context === null || this.masterGain === null) {
      return
    }
    const startTime = this.context.currentTime + delaySeconds
    const oscillator = this.context.createOscillator()
    oscillator.type = type
    oscillator.frequency.value = frequency

    const envelope = this.context.createGain()
    envelope.gain.setValueAtTime(0.0001, startTime)
    envelope.gain.exponentialRampToValueAtTime(peakGain, startTime + 0.015)
    envelope.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)

    oscillator.connect(envelope)
    envelope.connect(this.masterGain)
    oscillator.start(startTime)
    oscillator.stop(startTime + duration + 0.05)
  }

  /**
   * Play an oscillator that sweeps between two frequencies.
   *
   * @param {number} startFrequency - The starting frequency in hertz.
   * @param {number} endFrequency - The ending frequency in hertz.
   * @param {number} duration - The sweep length in seconds.
   * @param {number} peakGain - The envelope peak gain.
   * @returns {void}
   */
  private playSweep(startFrequency: number, endFrequency: number, duration: number, peakGain: number): void {
    if (this.context === null || this.masterGain === null) {
      return
    }
    const startTime = this.context.currentTime
    const oscillator = this.context.createOscillator()
    oscillator.type = 'sawtooth'
    oscillator.frequency.setValueAtTime(startFrequency, startTime)
    oscillator.frequency.exponentialRampToValueAtTime(endFrequency, startTime + duration)

    const filter = this.context.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.value = endFrequency * 0.8
    filter.Q.value = 1.4

    const envelope = this.context.createGain()
    envelope.gain.setValueAtTime(0.0001, startTime)
    envelope.gain.exponentialRampToValueAtTime(peakGain, startTime + duration * 0.3)
    envelope.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)

    oscillator.connect(filter)
    filter.connect(envelope)
    envelope.connect(this.masterGain)
    oscillator.start(startTime)
    oscillator.stop(startTime + duration + 0.05)
  }

  /**
   * Play a filtered burst of noise.
   *
   * @param {number} duration - The burst length in seconds.
   * @param {number} filterFrequency - The bandpass centre frequency in hertz.
   * @param {number} peakGain - The envelope peak gain.
   * @returns {void}
   */
  private playNoiseBurst(duration: number, filterFrequency: number, peakGain: number): void {
    if (this.context === null || this.masterGain === null || this.noiseBuffer === null) {
      return
    }
    const startTime = this.context.currentTime
    const source = this.context.createBufferSource()
    source.buffer = this.noiseBuffer

    const filter = this.context.createBiquadFilter()
    filter.type = 'bandpass'
    filter.frequency.value = filterFrequency
    filter.Q.value = 0.8

    const envelope = this.context.createGain()
    envelope.gain.setValueAtTime(peakGain, startTime)
    envelope.gain.exponentialRampToValueAtTime(0.0001, startTime + duration)

    source.connect(filter)
    filter.connect(envelope)
    envelope.connect(this.masterGain)
    source.start(startTime)
    source.stop(startTime + duration + 0.05)
  }
}
